import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { formatName } from 'app/utils/formatter/nameFormatter';
import { formatDate } from 'app/utils/formatter/dateTimeFormatter';
import { actions as ProcessEventsActions } from '../../../../Ducks/ProcessEvents.duck';
import { HasAccessTo, SearchInput } from 'app/components';
import { CONNECTION } from 'app/common/Authorization/entities';
import { DOWNLOAD } from 'app/common/Authorization/permissions';
import { IntlMessage } from '../../../../../../components/Intl';
import { EVENT_TYPE_MAPPER } from 'app/types';
import { GridItemStyled, GridStyled } from './ProcessEventsDialog.styles';
import ProcessEventsTableSkeleton from './ProcessEventsTableSkeleton';

const ProcessEventsDialog = ({ open, connectionId, batchId, batchMongoId, handleClose }) => {
  const dispatch = useDispatch();

  const { items, page, pageSize, totalCount, loading, error } = useSelector(state => state.ProcessEventsReducer);

  const [selectedEvents, setSelectedEvents] = React.useState([]);

  useEffect(() => {
    dispatch(ProcessEventsActions.clearEvents());
    dispatch(ProcessEventsActions.setConnectionId(connectionId));
    dispatch(ProcessEventsActions.setIds(batchId, batchMongoId));
    dispatch(ProcessEventsActions.requestData());
  }, [connectionId, batchId, batchMongoId, dispatch]);

  const handlePageChange = useCallback(
    (event, newPage) => {
      dispatch(ProcessEventsActions.changePage(newPage + 1));
    },
    [dispatch],
  );

  const handleRowsPerPageChange = useCallback(
    event => {
      dispatch(ProcessEventsActions.setPageSize(event.target.value));
    },
    [dispatch],
  );

  const downloadEvents = useCallback(() => {
    dispatch(ProcessEventsActions.downloadEvents(selectedEvents.length > 0 ? selectedEvents : null));
  }, [dispatch, selectedEvents]);

  const disabledIfNoAccessToConnectionDownload = !HasAccessTo(CONNECTION, DOWNLOAD);

  const formatEventType = React.useCallback(eventType => {
    return <FormattedMessage id={'events.type.' + EVENT_TYPE_MAPPER[eventType]} />;
  }, []);

  const handleEventSearch = useCallback(
    event => {
      dispatch(ProcessEventsActions.search(event.target.value));
    },
    [dispatch],
  );

  const handleEventTypeChange = useCallback(
    event => {
      dispatch(ProcessEventsActions.applyFilter('eventType', event.target.value));
    },
    [dispatch],
  );

  const handleArchivedChange = useCallback(
    event => {
      dispatch(ProcessEventsActions.applyFilter('archived', event.target.value));
    },
    [dispatch],
  );

  const handleToggleEvent = useCallback(
    eventMongoId => {
      const alreadyExists = selectedEvents.find(item => item === eventMongoId);

      setSelectedEvents(
        alreadyExists ? selectedEvents.filter(item => item !== eventMongoId) : [...selectedEvents, eventMongoId],
      );
    },
    [selectedEvents],
  );

  const areAllSelected = React.useMemo(() => {
    if (items.length === 0) return false;
    return items.every(event => selectedEvents.includes(event.mongoId));
  }, [items, selectedEvents]);

  const handleSelectAll = useCallback(() => {
    if (areAllSelected) {
      // Deselect all items on current page
      const currentPageIds = items.map(event => event.mongoId);
      setSelectedEvents(selectedEvents.filter(id => !currentPageIds.includes(id)));
    } else {
      // Select all items on current page
      const currentPageIds = items.map(event => event.mongoId);
      const newSelections = [...selectedEvents];
      currentPageIds.forEach(id => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedEvents(newSelections);
    }
  }, [areAllSelected, items, selectedEvents]);

  const eventTypes = React.useMemo(() => {
    return Object.keys(EVENT_TYPE_MAPPER).map(key => ({
      value: key,
      label: EVENT_TYPE_MAPPER[key],
    }));
  }, []);

  return (
    !error && (
      <Dialog open={open} onClose={() => handleClose()} fullWidth maxWidth="md" aria-labelledby="process-events-dialog">
        <DialogTitle id="process-events-dialog">
          <FormattedMessage id="connection.processes.events.dialogTitle" />
        </DialogTitle>
        <DialogContent>
          <GridStyled container spacing={3}>
            <GridItemStyled item xs={3}>
              <Select onChange={handleEventTypeChange} defaultValue="''" fullWidth>
                <MenuItem value="''">
                  <FormattedMessage id="all" />
                </MenuItem>
                {eventTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <FormattedMessage id={'events.type.' + type.label} />
                  </MenuItem>
                ))}
              </Select>
            </GridItemStyled>
            <GridItemStyled item xs={3}>
              <Select onChange={handleArchivedChange} defaultValue="''" fullWidth>
                <MenuItem value="''">
                  <FormattedMessage id="all" />
                </MenuItem>
                <MenuItem value="true">
                  <FormattedMessage id="yes" />
                </MenuItem>
                <MenuItem value="false">
                  <FormattedMessage id="no" />
                </MenuItem>
              </Select>
            </GridItemStyled>
            <GridItemStyled item xs={6}>
              <SearchInput onChange={handleEventSearch} initialSearchQuery={null} />
            </GridItemStyled>
          </GridStyled>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    color="primary"
                    checked={areAllSelected}
                    indeterminate={selectedEvents.length > 0 && !areAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="events.type" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="events.employee" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="events.archived" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="events.createdAt" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <ProcessEventsTableSkeleton rows={10} columns={5} />
              ) : (
                items.map(event => (
                  <TableRow hover key={event.mongoId}>
                    <TableCell>
                      <Checkbox
                        color="primary"
                        checked={selectedEvents.includes(event.mongoId)}
                        onChange={() => handleToggleEvent(event.mongoId)}
                      />
                    </TableCell>
                    <TableCell>{formatEventType(event.eventType)}</TableCell>
                    <TableCell>{formatName(event.hrEmployee.personName)}</TableCell>
                    <TableCell>
                      {event.archived ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />}
                    </TableCell>
                    <TableCell>{formatDate(event.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <TablePagination
            component="div"
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[100, 500, 1000, 10000]}
          />
          <Button onClick={downloadEvents} disabled={disabledIfNoAccessToConnectionDownload}>
            {selectedEvents.length > 0 ? (
              <FormattedMessage id="events.download" />
            ) : (
              <FormattedMessage id="events.downloadAll" />
            )}
          </Button>
          <Button onClick={() => handleClose()} color="secondary">
            <IntlMessage value={'close'} />
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

ProcessEventsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  batchId: PropTypes.string.isRequired,
  batchMongoId: PropTypes.string.isRequired,
  connectionId: PropTypes.string.isRequired,
};

export default ProcessEventsDialog;
